<template>
  <div class="wrapper p-3">
    <data-filter ref="dataFilter" :on-apply-filter="onApplyFilter" />

    <template v-if="isLoading">
      <div class="text-center mt-5">
        <b-spinner label="Spinning"></b-spinner>
      </div>
    </template>

    <template v-else>
      <!-- <div v-if="selectedAsset" class="mb-3">
        <h5>Selected Asset (Debug):</h5>
        <json-viewer :value="selectedAsset" :expand-depth="2" copyable boxed></json-viewer>
      </div>
      <div v-if="rawSummary" class="mb-3">
        <h5>Raw API Response (Debug):</h5>
        <json-viewer :value="rawSummary" :expand-depth="5" copyable boxed></json-viewer>
      </div> -->

      <div class="row">
        <div class="col-md-6 mb-4" v-for="(summaryData, engineName) in engineSummaries" :key="engineName">
            <div class="iq-card h-100">
              <div class="iq-card-header pt-2">
                <div class="iq-card-title">
                  <h5 class="card-title text-capitalize">{{ engineName }} Summary</h5>
                </div>
              </div>
              <div class="iq-card-body">
                <table class="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th class="text-center">RPM Classification</th>
                      <th class="text-center">Duration</th>
                      <th class="text-center">Percentage</th>
                      <th class="text-center">Fuel Consumption</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in summaryData" :key="index">
                      <td class="text-center">{{ item.mode }}</td>
                      <td class="text-center">{{ item.duration }}</td>
                      <td class="text-center">{{ item.percentage }}</td>
                      <td class="text-center">{{ item.consumption }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
      </div>

      <div class="iq-card mt-2 mb-4" v-for="(summaryData, engineName) in totalSummary" :key="engineName">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title text-capitalize">{{ engineName }} Summary</h5>
            </div>
          </div>
          <div class="iq-card-body">
            <table class="table table-striped table-bordered">
              <thead>
                <tr>
                  <th class="text-center">RPM Clasification</th>
                  <th class="text-center">Duration</th>
                  <th class="text-center">Percentage</th>
                  <th class="text-center">Fuel Consumption</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in summaryData" :key="index">
                  <td class="text-center">{{ item.mode }}</td>
                  <td class="text-center">{{ item.duration }}</td>
                  <td class="text-center">{{ item.percentage }}</td>
                  <td class="text-center">{{ item.consumption }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      <div v-if="rawSeries" class="iq-card mt-4">
        <div class="iq-card-header pt-2">
          <div class="iq-card-title">
            <h5 class="card-title">RPM Series Details</h5>
          </div>
        </div>
        <div class="iq-card-body pt-2 pb-5 mb-5">
          <div class="table-responsive">
            <div style="height: 70vh; overflow-y: auto;">
              <table class="table table-striped table-sm table-hover table-bordered text-center">
                <thead class="thead-light">
                  <tr>
                    <th v-for="header in seriesTableHeaders" :key="header.value" class="text-center text-capitalize">{{ header.text }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in seriesTableRows" :key="index">
                    <td v-for="(header, colIndex) in seriesTableHeaders" :key="header.value + index" class="text-center"
                        :class="colIndex === 0 ? 'sticky-column' : ''">
                      {{ row[header.value] }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import { initAssetSummary } from '@/util/asset-summary-loader';
import filterDeepLink from '@/mixins/filterDeepLink';
import { formatNum } from '@/util/number';
import DataFilter from '../../components/DataFilter';
import OperationModeSvc from '../../services/OperationModeSvc';

export default {
  name: 'OperationModeSummary',
  mixins: [filterDeepLink],
  filterUrlGroups: ['asset', 'date', 'interval'],
  components: {
    DataFilter,
  },
  data() {
    return {
      isLoading: false,
      summary: {},
      rawSummary: null,
      rawSeries: null,
    };
  },
  computed: {
    ...mapState('asset', {
      allAssets: 'assets',
    }),
    ...mapGetters('filter', ['currentMassId']),
    selectedAsset() {
      if (!this.currentMassId || !this.allAssets) return null;
      return this.allAssets.find((a) => a.massId === this.currentMassId);
    },
    dateRange: {
      get() {
        const { start, end } = this.$store.getters['filter/date'];
        const startDate = new Date(moment(start).valueOf());
        const endDate = new Date(moment(end).valueOf());

        return { start: startDate, end: endDate };
      },
    },
    engineSummaries() {
      return Object.fromEntries(
        Object.entries(this.summary).filter(([key]) => key !== 'TOTAL'),
      );
    },
    totalSummary() {
      return Object.fromEntries(
        Object.entries(this.summary).filter(([key]) => key === 'TOTAL'),
      );
    },
    seriesTableHeaders() {
      if (!this.rawSeries || !this.rawSeries.data) return [];

      const timestamps = Object.keys(this.rawSeries.data);
      if (timestamps.length === 0) return [];

      const firstEntry = this.rawSeries.data[timestamps[0]];
      const engineNames = Object.keys(firstEntry);

      const headers = [{ text: 'Timestamp', value: 'timestamp' }];
      engineNames.forEach((name) => {
        headers.push(
          { text: `${name} Mode`, value: `${name.toLowerCase()}_mode` },
          { text: `${name} RPM`, value: `${name.toLowerCase()}_rpm` },
        );
      });

      return headers;
    },
    seriesTableRows() {
      if (!this.rawSeries || !this.rawSeries.data) return [];

      return Object.entries(this.rawSeries.data).map(([timestamp, engines]) => {
        const row = {
          timestamp: moment.unix(timestamp).format('YYYY-MM-DD HH:mm:ss Z'),
        };
        Object.entries(engines).forEach(([engineName, engineData]) => {
          row[`${engineName.toLowerCase()}_mode`] = engineData.mode;
          row[`${engineName.toLowerCase()}_rpm`] = engineData.rpm;
        });
        return row;
      });
    },
  },
  async mounted() {
    this.isLoading = true;
    // Halaman ini nol referensi ke currAsset.summary — cuma butuh daftar device.
    await initAssetSummary(this.$store, { withDevices: true, skipSummary: true });
    await this.fetchOperationModeSummary();
    this.syncFilterUrl();
    this.isLoading = false;
  },
  methods: {
    async onApplyFilter() {
      this.isLoading = true;
      await this.fetchOperationModeSummary();
      this.syncFilterUrl();
      this.isLoading = false;
    },
    async fetchOperationModeSummary() {
      this.summary = {};
      this.rawSummary = null;
      this.rawSeries = null;

      if (!this.selectedAsset) {
        return;
      }

      const rpmDevice = this.selectedAsset.devices.find((d) => d.devcType === 'rpm');

      if (!rpmDevice) {
        console.warn('No RPM device found for the selected asset.');
        return;
      }

      const { start, end } = this.dateRange;
      const startTime = moment(start).unix();
      const endTime = moment(end).unix();

      try {
        const [summaryResponse, seriesResponse] = await Promise.all([
          OperationModeSvc.getOperationModeSummary({
            start: startTime,
            end: endTime,
            devcId: rpmDevice.devcId,
          }),
          OperationModeSvc.getOperationModeSeries({
            start: startTime,
            end: endTime,
            devcId: rpmDevice.devcId,
          }),
        ]);

        if (summaryResponse.data.status?.errorCode === 412) {
          this.$toast.error(summaryResponse.data.status.message || 'You don\'t have access to this vessel\'s operation mode data.');
          this.summary = {};
          this.rawSummary = null;
          this.rawSeries = null;
          return;
        }

        const summaryData = summaryResponse.data.data;
        this.rawSummary = summaryData;
        const processSummary = (modes, totalRunningSeconds) => {
          if (!modes) return [];
          return Object.keys(modes).map((modeKey) => {
            const modeData = modes[modeKey];
            const percentage = totalRunningSeconds > 0
              ? formatNum(((modeData.runningSeconds / totalRunningSeconds) * 100), 2)
              : 0;
            const hours = Math.floor(modeData.runningSeconds / 3600);
            const minutes = Math.floor((modeData.runningSeconds % 3600) / 60);
            return {
              mode: modeKey,
              duration: `${hours}h ${minutes}m`,
              percentage: `${percentage}%`,
              consumption: formatNum((modeData.value || 0), 2),
            };
          });
        };
        const processedSummary = {};
        if (summaryData.engine) {
          Object.keys(summaryData.engine).forEach((engineKey) => {
            const engineModes = summaryData.engine[engineKey];
            const engineTotalSeconds = Object.values(engineModes).reduce((acc, mode) => acc + mode.runningSeconds, 0);
            processedSummary[engineKey] = processSummary(engineModes, engineTotalSeconds);
          });
        }
        if (summaryData.total && summaryData.total.mode) {
          processedSummary.TOTAL = processSummary(summaryData.total.mode, summaryData.total.runningSeconds);
        }
        this.summary = processedSummary;

        this.rawSeries = seriesResponse.data.data;
      } catch (error) {
        console.error('Failed to fetch operation mode summary or series:', error);
        this.summary = {};
        this.rawSummary = null;
        this.rawSeries = null;
      }
    },
  },
};
</script>

<style scoped>
.wrapper {
  position: relative;
}

.sticky-column {
  position: sticky;
  left: 0;
}

.sticky-column:nth-child(odd) {
  background-color: #f5f5f5 !important;
}

.sticky-column:nth-child(even) {
  background-color: #f5f5f5 !important;
}

.table {
  border-collapse: collapse;
}

th {
  position: sticky !important;
  top: 0 !important;
  border: 1px solid !important;
  padding: 8px !important;
  text-align: center !important;
  vertical-align: middle !important;
  font-size: 14.5px !important;
  background-color: #767476 !important;
  color: #FAFAFA !important;
}

th:first-child {
  position: sticky !important;
  left: 0 !important;
  z-index: 5;
}
</style>
